import { FC, useState } from "react";
import { Domain } from "@/types/domain";
import { FileText, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface CircularDomainDialProps {
  domains: Domain[];
}

const getGsaKnowledgeBases = (symbol: string) => {
  switch(symbol) {
    case "Design Automation":
      return [
        { title: "Requirements Generation: Floor Plan & Space Requirements", url: "/gsa-kb/requirements-generation" },
        { title: "Design Tools: Timing & ROM Cost Generation", url: "/gsa-kb/design-tools" },
        { title: "Subject Matter Expertise Customization: Security & Historic Preservation", url: "/gsa-kb/subject-matter-expertise" },
        { title: "Government Standards Integration Framework", url: "/gsa-kb/government-standards" },
        { title: "Site-Specific Constraints Management", url: "/gsa-kb/site-specific-constraints" }
      ];
    case "Space &/or Furniture Layout":
      return [
        { title: "Workplace Modeling: Optimal Workspace Configurations", url: "/gsa-kb/space-furniture-layout" },
        { title: "Scenario Testing: Open Office vs Hybrid Strategies", url: "/gsa-kb/space-furniture-layout" },
        { title: "Automated Space Planning: GSA Standards Implementation", url: "/gsa-kb/space-furniture-layout" },
        { title: "Generative Furniture Design: Modular & Ergonomic Solutions", url: "/gsa-kb/space-furniture-layout" },
        { title: "Furniture Reconfiguration & Re-use Guidelines", url: "/gsa-kb/space-furniture-layout" }
      ];
    case "Analysis Tools":
      return [
        { title: "Zoning & Code Compliance: Automated Checks", url: "/gsa-kb/analysis-tools" },
        { title: "Space Utilization & Circulation Analysis", url: "/gsa-kb/analysis-tools" },
        { title: "Daylighting Analysis: Optimization Framework", url: "/gsa-kb/analysis-tools" },
        { title: "View Analysis: Building Orientation & Window Placement", url: "/gsa-kb/analysis-tools" },
        { title: "Acoustic Analysis: HVAC & Partition Construction", url: "/gsa-kb/analysis-tools" },
        { title: "Automated Plan Checking: GSA Standards Compliance", url: "/gsa-kb/analysis-tools" },
        { title: "Clash Detection & Conflict Resolution", url: "/gsa-kb/analysis-tools" },
        { title: "Predictive Issue Detection: Cost Overrun Prevention", url: "/gsa-kb/analysis-tools" },
        { title: "Smart Building Management: HVAC & Lighting Optimization", url: "/gsa-kb/analysis-tools" }
      ];
    case "Cost and Schedule Estimation":
      return [
        { title: "Material & Cost Optimization: Contract Specifications", url: "/gsa-kb/cost-schedule-estimation" },
        { title: "Procurement & Bid Support: Vendor Analysis", url: "/gsa-kb/cost-schedule-estimation" },
        { title: "Schedule Scenarios: Resource Loaded Planning", url: "/gsa-kb/cost-schedule-estimation" },
        { title: "Rough Order of Magnitude Costing", url: "/gsa-kb/cost-schedule-estimation" },
        { title: "Phased Project Execution Timeline", url: "/gsa-kb/cost-schedule-estimation" }
      ];
    case "Visualization & Rendering":
      return [
        { title: "3D Modeling & Visualization: Realistic Virtual Models", url: "/gsa-kb/visualization-rendering" },
        { title: "Style Transfer & Concept Art: Space Visualization", url: "/gsa-kb/visualization-rendering" },
        { title: "Furniture & Space Layout Rendering", url: "/gsa-kb/visualization-rendering" },
        { title: "Concept Renderings & Mood Boards", url: "/gsa-kb/visualization-rendering" }
      ];
    case "Post-Occupancy Monitoring":
      return [
        { title: "Employee Experience & Feedback Integration", url: "/gsa-kb/post-occupancy-monitoring" },
        { title: "Predictive Maintenance: Building Systems Monitoring", url: "/gsa-kb/post-occupancy-monitoring" },
        { title: "Building System Monitoring: Performance Dashboard", url: "/gsa-kb/post-occupancy-monitoring" },
        { title: "Occupant Comfort & Building Performance Feedback", url: "/gsa-kb/post-occupancy-monitoring" },
        { title: "Continuous Improvement Implementation Strategies", url: "/gsa-kb/post-occupancy-monitoring" }
      ];
    default:
      return [
        { title: `${symbol} Knowledge Base 1`, url: "#" },
        { title: `${symbol} Knowledge Base 2`, url: "#" },
        { title: `${symbol} Knowledge Base 3`, url: "#" }
      ];
  }
};

const CircularDomainDial: FC<CircularDomainDialProps> = ({ domains }) => {
  const [hoveredDomain, setHoveredDomain] = useState<string | null>(null);
  const [isHoveringPopover, setIsHoveringPopover] = useState(false);
  const navigate = useNavigate();

  const handleKnowledgeBaseClick = (url: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(url);
  };

  // Calculate position for each domain box in the circle
  const getPosition = (index: number, total: number) => {
    const angle = (360 / total) * index - 90; // Start from top
    const radius = 140; // Distance from center
    const radian = (angle * Math.PI) / 180;
    const x = Math.cos(radian) * radius;
    const y = Math.sin(radian) * radius;
    return { x, y, angle };
  };

  return (
    <div className="grid grid-cols-3 gap-4">
      {domains.map((domain, index) => (
        <div 
          key={domain.symbol}
          className="relative"
          onMouseEnter={() => setHoveredDomain(domain.symbol)}
          onMouseLeave={() => {
            if (!isHoveringPopover) {
              setHoveredDomain(null);
            }
          }}
        >
          <div className="cursor-pointer transition-all duration-300 hover:scale-105">
            <div className="bg-blue-600 text-white p-4 rounded-lg shadow-lg hover:bg-black transition-colors duration-200 h-24 flex flex-col items-center justify-center">
              <div className="text-xs font-bold text-center leading-tight">
                {domain.symbol}
              </div>
            </div>
          </div>
          
          {hoveredDomain === domain.symbol && (
            <div 
              className="absolute top-full left-0 mt-2 w-80 p-4 bg-white border border-gray-200 shadow-lg rounded-lg z-50"
              onMouseEnter={() => setIsHoveringPopover(true)}
              onMouseLeave={() => {
                setIsHoveringPopover(false);
                setHoveredDomain(null);
              }}
            >
              <h3 className="text-sm font-semibold mb-3">{domain.name}</h3>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {getGsaKnowledgeBases(domain.symbol).map((kb, kbIndex) => (
                  <div 
                    key={kbIndex}
                    className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer text-xs transition-colors"
                    onClick={(e) => handleKnowledgeBaseClick(kb.url, e)}
                  >
                    <FileText className="h-3 w-3 text-blue-600 flex-shrink-0" />
                    <span className="flex-1">{kb.title}</span>
                    <ExternalLink className="h-3 w-3 text-gray-400 flex-shrink-0" />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default CircularDomainDial;